import { useEffect, useRef, useState } from 'react';

/**
 * Reports whether the element is currently "active", i.e. its centre sits close
 * to the vertical centre of the viewport. The state toggles on/off as the
 * element crosses the centre band, so scroll-linked effects (captions, zooms)
 * replay every time the element comes back.
 *
 * Using the viewport centre (instead of a plain "is it on screen" check) is
 * important on phones: tall images stay on screen for a long scroll distance,
 * so a simple in-view check would look permanently "stuck".
 *
 * @param tolerance Fraction of the viewport height within which the element
 *                  centre counts as active (0.35 = middle 70% band).
 */
export default function useInView<T extends HTMLElement>(tolerance = 0.35) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    let frame = 0;

    const evaluate = () => {
      frame = 0;
      const rect = node.getBoundingClientRect();
      const viewport =
        window.innerHeight || document.documentElement.clientHeight || 0;
      const elementCentre = rect.top + rect.height / 2;
      const distance = Math.abs(elementCentre - viewport / 2);
      setInView(distance <= viewport * tolerance);
    };

    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(evaluate);
    };

    evaluate();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [tolerance]);

  return { ref, inView };
}